import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { message, Dropdown } from "antd";
import {
	CheckOutlined,
	ClockCircleOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	MoreOutlined,
} from "@ant-design/icons";
import { getPlatformNameFromUrl } from "@/shared/utils/platformLinks";
import { CREATOR_CATEGORIES, CREATOR_TIERS } from "@/api/creator";
import { creatorListAPI, CreatorAssignmentApproval } from "@/api/creatorList";
import {
	formatRateDisplayValue,
	formatRateMoneyDisplayValue,
	isRateInputValid,
	sanitizeRateInputValue,
} from "@/shared/utils/rateInput";
import { useFinalRateEditing } from "../../hooks/useFinalRateEditing";
import RationaleModal from "./RationaleModal";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import { Icon } from "@/shared/components/Icon";

import { CreatorInterestSelect } from "./CreatorInterestSelect";
import { StyledSelect } from "@/shared/components/Select/styledSelect";
import { getCreatorInterestStatusLabel, normalizeCreatorInterestStatus } from "../../utils/interestStatusHelper";
import type { CreatorInterestStatus } from "@/shared/types/campaign";
import type { Creator } from "@/shared/types/campaign/creator";
import { useAssignmentContactEmail } from "../../hooks/useAssignmentContactEmail";

const StyledDropdownMenu = styled.div`
	width: 170px;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0px 4px 12px 0px rgba(21, 89, 165, 0.23);
	overflow: hidden;
`;

const StyledDropdownItem = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 18px;
	border: none;
	background: #ffffff;
	color: #35373d;
	font-size: 14px;
	text-align: left;
	cursor: pointer;
	opacity: 1;
	transition: background 0.2s;

	&:not(:last-child) {
		margin-bottom: 1px;
	}
	&:hover {
		background: #f7f8fc;
	}
`;

const TableContainer = styled.div`
	width: 100%;
	margin-top: 1rem;
	overflow-x: auto;
`;

const Table = styled.table`
	width: 100%;
	background: white;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	border-collapse: collapse;
`;

const TableHeader = styled.thead`
	background: #f5f5f5;
`;

const TableHeaderCell = styled.th`
	padding: 12px 16px;
	text-align: left;
	font-weight: 600;
	color: #333;
	font-size: 14px;
	border-bottom: 2px solid #e0e0e0;
	white-space: nowrap;

	&:first-child {
		width: 140px;
	}

	&:nth-child(2),
	&:nth-child(3),
	&:nth-child(4),
	&:nth-child(5),
	&:nth-child(6),
	&:nth-child(7),
	&:nth-child(8) {
		width: 100px;
	}

	&:nth-child(9) {
		width: 80px;
	}

	&:last-child {
		width: 60px;
	}
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $isDragging?: boolean; $isCustomizing?: boolean; $isVisible?: boolean }>`
	border-bottom: 1px solid #f0f0f0;
	cursor: ${(props) => (props.$isCustomizing ? "move" : "default")};
	opacity: ${(props) => (props.$isDragging ? 0 : 1)};
	background: ${(props) => (props.$isVisible === false ? "#E6E6E6" : "transparent")};

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: ${(props) => (props.$isVisible === false ? "#d9d9d9" : "#fafafa")};
	}
`;

const DragHandle = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	cursor: move;
	margin-right: 8px;
	flex-shrink: 0;
`;

const DragIcon = styled(Icon)`
	width: 16px;
	height: 16px;
	opacity: 0.8;
`;

const TableCell = styled.td`
	padding: 12px 16px;
	color: #666;
	font-size: 14px;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
	white-space: nowrap;
`;

const CreatorNameCell = styled(TableCell)<{ $isCustomizing?: boolean }>`
	font-weight: 500;
	font-size: 13px;
	color: #333;
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 12px 16px !important;
`;

const ProfileImage = styled(ResponsiveAvatar)`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const NameText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

const NameWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
`;

const SettingsIcon = styled(Icon)`
	width: 20px;
	height: 20px;
	cursor: pointer;
	opacity: 0.6;
	transition: opacity 0.2s;

	&:hover {
		opacity: 1;
	}
`;

const ActionIcons = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
`;

const SmallToggle = styled.div<{ $active: boolean; $disabled?: boolean }>`
	width: 32px;
	height: 18px;
	border-radius: 9px;
	background: ${(props) => (props.$active ? "#4460ef" : "#d9d9d9")};
	position: relative;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
	transition:
		background 0.2s,
		opacity 0.2s;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${(props) => (props.$active ? "16px" : "2px")};
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: white;
		transition: left 0.2s;
	}
`;

const LinkCell = styled(TableCell)`
	a {
		color: #1677ff;
		text-decoration: none;
		font-weight: 500;
		cursor: pointer;
		display: inline;

		&:hover {
			text-decoration: underline;
			color: #4096ff;
		}
	}
`;

const RationaleCellContent = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	transition: opacity 0.15s;
	&:hover {
		opacity: 0.8;
	}
`;

const STATUS_STYLES: Record<string, { bg: string; color: string; border: string }> = {
	success: { bg: "#e6f7e6", color: "#389e0d", border: "#b7eb8f" },
	error: { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e" },
	pending: { bg: "#fff7e6", color: "#d48806", border: "#ffe58f" },
	info: { bg: "#e6f4ff", color: "#1677ff", border: "#91caff" },
	warning: { bg: "#fff7e6", color: "#fa8c16", border: "#ffd591" },
};

const StatusBadge = styled.span<{ $variant: string }>`
	display: inline-flex;
	align-items: center;
	padding: 2px 8px;
	border-radius: 10px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	background: ${(props) => STATUS_STYLES[props.$variant]?.bg || "#f5f5f5"};
	color: ${(props) => STATUS_STYLES[props.$variant]?.color || "#666"};
	border: 1px solid ${(props) => STATUS_STYLES[props.$variant]?.border || "#d9d9d9"};
`;

const ApprovalCell = styled.span<{ $status: "approved" | "rejected" | "pending" }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 500;
	white-space: nowrap;
	color: #333;

	.anticon {
		font-size: 13px;
		color: ${(props) =>
			props.$status === "approved" ? "#66B026E0" : props.$status === "rejected" ? "#D80027" : "#8c8c8c"};
	}
`;

const RateInputShell = styled.div<{ $invalid?: boolean }>`
	position: relative;
	width: 100%;
	margin-top: 4px;
	height: 28px;
	min-width: 120px;
	display: flex;
	align-items: center;
	border: 1px solid ${(props) => (props.$invalid ? "#ff4d4f" : "#cdd4f6")};
	border-radius: 8px;
	background: #fff;
	transition:
		border-color 0.2s,
		box-shadow 0.2s;

	&:focus-within,
	&:hover {
		border-color: ${(props) => (props.$invalid ? "#ff4d4f" : "#8193f1")};
	}
`;

const RateSuffix = styled.span`
	position: absolute;
	right: 10px;
	font-size: 12px;
	color: #666;
	pointer-events: none;
`;

const RateInput = styled.input`
	width: 100%;
	height: 24px;
	border: none;
	outline: none;
	font-size: 13px;
	color: #303030;
	padding: 0 42px 0 10px;
	border-radius: 8px;
`;
const EmailInput = styled.input`
	width: 100%;
	height: 24px;
	border: none;
	outline: none;
	font-size: 13px;
	color: #303030;
	padding: 0 10px;
	border-radius: 8px;

	&:focus {
		outline: none;
	}
`;
const EmailError = styled.span`
	display: block;
	color: #d30024;
	font-size: 11px;
	line-height: 16px;
`;

interface CreatorTableProps {
	creators: Creator[];
	isCustomizing: boolean;
	creatorVisibility: { [key: string]: boolean };
	onCreatorVisibilityToggle: (creatorId: string) => void;
	onSettingsClick: (creatorId: string) => void;
	onOrderChange?: (newOrder: Creator[]) => void;
	displayFields?: Array<{ key: string; label: string }>;
	onRemoveCreator?: (assignmentId: string, creatorName?: string) => void;
	isClientOnly?: boolean;
	isOutreach?: boolean;
	campaignId?: string;
	blockId?: string;
	finalRateCurrency?: string;
	finalRateConvertedCurrency?: string;
	canManage?: boolean;
	onCategoryTierChange?: (creatorId: string, field: "categories" | "tier", value: string[] | string) => void;
	onUpdateCreator?: (update: { id: string; recommendation?: string[] }) => void;
	onInterestChange?: (creatorId: string, interested: CreatorInterestStatus | undefined) => void;
	onContactEmailChange?: (assignmentId: string, contactEmail: string | null) => void;
}

const DEFAULT_DISPLAY_FIELDS: Array<{ key: string; label: string }> = [
	{ key: "category", label: "Category" },
	{ key: "creatorTier", label: "Creator Tier" },
	{ key: "sampleContent", label: "Sample Content" },
	{ key: "location", label: "Location" },
];

const formatValue = (value: any, fieldKey?: string) => {
	if (value === null || value === undefined || value === "") {
		return "—";
	}

	// Handle Sample Content as clickable links
	if (fieldKey === "sampleContent") {
		if (Array.isArray(value)) {
			return value.map((url, index) => (
				<React.Fragment key={`link-${index}`}>
					<a href={url} target="_blank" rel="noopener noreferrer">
						{getPlatformNameFromUrl(String(url))}
					</a>
					{index < value.length - 1 && " | "}
				</React.Fragment>
			));
		}
		if (typeof value === "string" && value.startsWith("http")) {
			return (
				<a href={value} target="_blank" rel="noopener noreferrer">
					{getPlatformNameFromUrl(value)}
				</a>
			);
		}
	}

	if (Array.isArray(value)) {
		return value.join(", ");
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return value;
};

const HIDDEN_CURRENCY_KEYS = new Set([
	"FinalRateConvertedCurrency",
	"DefaultRateConvertedCurrency",
	"finalRateConvertedCurrency",
	"defaultRateConvertedCurrency",
]);

export const CreatorTable: React.FC<CreatorTableProps> = ({
	creators,
	isCustomizing,
	creatorVisibility,
	onCreatorVisibilityToggle,
	onSettingsClick,
	onOrderChange,
	displayFields = DEFAULT_DISPLAY_FIELDS,
	onRemoveCreator,
	isClientOnly = false,
	isOutreach = false,
	canManage = false,
	campaignId,
	blockId,
	finalRateCurrency = "USD",
	finalRateConvertedCurrency = "AUD",
	onCategoryTierChange,
	onUpdateCreator,
	onInterestChange,
	onContactEmailChange,
}) => {
	const [rationale, setRationale] = useState<null | { creatorId: string; assignmentId: string; text?: string }>(null);
	const [submittingRationale, setSubmittingRationale] = useState(false);
	const [orderedCreators, setOrderedCreators] = useState<Creator[]>(creators);
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [approvals, setApprovals] = useState<{ [assignmentId: string]: CreatorAssignmentApproval | null }>({});
	const contactEmail = useAssignmentContactEmail(campaignId, blockId, onContactEmailChange);
	const {
		rateDrafts,
		setRateDrafts,
		convertedRateDrafts,
		setConvertedRateDrafts,
		rateInvalid,
		setRateInvalid,
		convertedRateInvalid,
		setConvertedRateInvalid,
		debouncedSaveFinalRates,
		saveFinalRates,
	} = useFinalRateEditing({
		creators,
		campaignId,
		blockId,
		creatorVisibility,
		finalRateCurrency,
		finalRateConvertedCurrency,
	});

	useEffect(() => {
		setOrderedCreators(creators);
	}, [creators]);

	useEffect(() => {
		if (!campaignId || !blockId) return;
		creators.forEach((creator) => {
			const assignmentId = creator.assignmentId || creator.assignment?.id;
			if (!assignmentId || approvals[assignmentId] !== undefined) return;
			creatorListAPI
				.getCreatorAssignmentApprovals(campaignId, blockId, assignmentId)
				.then((allApprovals) => {
					const clientApproval =
						allApprovals.find((a) => (a.authorRoles ?? []).some((r) => r?.toLowerCase() === "client")) ??
						null;
					setApprovals((prev) => ({ ...prev, [assignmentId]: clientApproval }));
				})
				.catch(() => setApprovals((prev) => ({ ...prev, [assignmentId]: null })));
		});
	}, [creators, campaignId, blockId]);

	const handleDragStart = (e: React.DragEvent, index: number) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e: React.DragEvent, index: number) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === index) return;

		const newCreators = [...orderedCreators];
		const draggedItem = newCreators[draggedIndex];
		newCreators.splice(draggedIndex, 1);
		newCreators.splice(index, 0, draggedItem);
		setOrderedCreators(newCreators);
		setDraggedIndex(index);

		if (onOrderChange) {
			onOrderChange(newCreators);
		}
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const displayCreators = isCustomizing ? orderedCreators : creators;
	const filteredFields = displayFields.filter((f) => !HIDDEN_CURRENCY_KEYS.has(f.key));

	const extraColumns: Array<{ key: string; label: string }> = isOutreach
		? [
				{ key: "_approval", label: "Client Approval" },
				{ key: "_comment", label: "Client Comment" },
				{ key: "_finalRate", label: "Creator Rate" },
				{ key: "_finalConvertedRate", label: "Converted Rate" },
				{ key: "_creatorInterest", label: "Reachout Status" },
			]
		: [
				{ key: "_rationale", label: "Rationale For Proposing" },
				{ key: "_approval", label: "Client Approval" },
				{ key: "_comment", label: "Client Comment" },
			];

	return (
		<TableContainer>
			<Table>
				<TableHeader>
					<tr>
						<TableHeaderCell>Creator Name</TableHeaderCell>
						<TableHeaderCell>Email Address</TableHeaderCell>
						{filteredFields.map((field) => (
							<TableHeaderCell key={`header-${field.key}`}>{field.label}</TableHeaderCell>
						))}
						{extraColumns.map((col) => (
							<TableHeaderCell key={`header-${col.key}`}>{col.label}</TableHeaderCell>
						))}
						<TableHeaderCell>Visible</TableHeaderCell>
						{!isOutreach && <TableHeaderCell></TableHeaderCell>}
					</tr>
				</TableHeader>
				<TableBody>
					{displayCreators.map((creator, index) => {
						const isVisible = creatorVisibility[creator.id] ?? true;

						return (
							<TableRow
								key={creator.id}
								$isCustomizing={isCustomizing}
								$isDragging={draggedIndex === index}
								$isVisible={isVisible}
								draggable={isCustomizing}
								onDragStart={(e) => handleDragStart(e, index)}
								onDragOver={(e) => handleDragOver(e, index)}
								onDragEnd={handleDragEnd}
							>
								<TableCell>
									<CreatorNameCell $isCustomizing={isCustomizing}>
										<NameWrapper>
											{isCustomizing && (
												<DragHandle>
													<DragIcon
														name="moving"
														alt="Drag"
														style={{
															filter: "invert(36%) sepia(84%) saturate(2572%) hue-rotate(315deg) brightness(98%) contrast(96%)",
														}}
													/>
												</DragHandle>
											)}
											<ProfileImage
												displaySize={32}
												src={creator.img_url || "/default_avatar.png"}
												alt={creator.name}
												onError={(e) => {
													const t = e.currentTarget;
													if (t.src.endsWith("/default_avatar.png")) return;
													t.src = "/default_avatar.png";
												}}
											/>
											<NameText>{creator.name}</NameText>
										</NameWrapper>
									</CreatorNameCell>
								</TableCell>
								<TableCell>
									{(() => {
										const assignmentId = creator.assignmentId || creator.assignment?.id;
										const savedEmail =
											creator.contactEmail ?? creator.assignment?.contactEmail ?? "";
										if (!assignmentId || !canManage || isClientOnly) return savedEmail || "—";
										return (
											<>
												<RateInputShell $invalid={Boolean(contactEmail.errors[assignmentId])}>
													<EmailInput
														type="email"
														aria-label={`Campaign contact email for ${creator.name}`}
														disabled={contactEmail.saving[assignmentId]}
														value={contactEmail.drafts[assignmentId] ?? savedEmail}
														onChange={(event) =>
															contactEmail.setDraft(assignmentId, event.target.value)
														}
														onBlur={() => void contactEmail.save(assignmentId, savedEmail)}
													/>
												</RateInputShell>
												{contactEmail.errors[assignmentId] && (
													<EmailError>{contactEmail.errors[assignmentId]}</EmailError>
												)}
											</>
										);
									})()}
								</TableCell>
								{filteredFields.map((field) => {
									const CellComponent = field.key === "sampleContent" ? LinkCell : TableCell;

									if (
										!isClientOnly &&
										canManage &&
										onCategoryTierChange &&
										field.key === "category"
									) {
										const raw = creator[field.key];
										const currentVal = Array.isArray(raw)
											? raw[0] || ""
											: typeof raw === "string"
												? raw
												: "";
										return (
											<TableCell key={`${creator.id}-${field.key}`}>
												<StyledSelect
													value={currentVal || undefined}
													onChange={(val: string) => {
														onCategoryTierChange(
															creator.id,
															"categories",
															val ? [val] : [],
														);
													}}
													size="small"
													placeholder="Category"
													style={{ width: 150, maxWidth: 150 }}
													options={CREATOR_CATEGORIES.map((c) => ({ value: c, label: c }))}
													allowClear
												/>
											</TableCell>
										);
									}

									if (
										!isClientOnly &&
										canManage &&
										onCategoryTierChange &&
										field.key === "creatorTier"
									) {
										const currentVal = creator[field.key] || "";
										return (
											<TableCell key={`${creator.id}-${field.key}`}>
												<StyledSelect
													value={currentVal || undefined}
													onChange={(value: string) => {
														onCategoryTierChange(creator.id, "tier", value);
													}}
													size="small"
													placeholder="Tier"
													style={{ width: 100, maxWidth: 100 }}
													options={CREATOR_TIERS.map((t) => ({ value: t, label: t }))}
													allowClear
												/>
											</TableCell>
										);
									}

									return (
										<CellComponent key={`${creator.id}-${field.key}`}>
											{formatValue(creator[field.key], field.key)}
										</CellComponent>
									);
								})}
								{extraColumns.map((col) => {
									if (col.key === "_finalRate") {
										const assignmentId = creator.assignmentId || creator.assignment?.id;
										const readOnly = isClientOnly || !canManage || !assignmentId;

										return (
											<TableCell key={`${creator.id}-${col.key}`}>
												{readOnly ? (
													(() => {
														const value =
															creator.finalRate ?? creator.assignment?.finalRate;
														return value === null || value === undefined
															? "—"
															: `${finalRateCurrency} ${formatRateMoneyDisplayValue(value, finalRateCurrency)}`;
													})()
												) : (
													<RateInputShell $invalid={rateInvalid[creator.id]}>
														<RateSuffix>{finalRateCurrency}</RateSuffix>
														<RateInput
															placeholder="0.00"
															value={
																rateDrafts[creator.id] ??
																formatRateDisplayValue(
																	creator.finalRate ?? creator.assignment?.finalRate,
																	finalRateCurrency,
																)
															}
															onChange={(e) => {
																const nextValue = sanitizeRateInputValue(
																	e.target.value,
																	finalRateCurrency,
																);
																setRateDrafts((prev) => ({
																	...prev,
																	[creator.id]: formatRateDisplayValue(
																		nextValue,
																		finalRateCurrency,
																	),
																}));
																if (
																	rateInvalid[creator.id] &&
																	isRateInputValid(
																		nextValue.trim(),
																		finalRateCurrency,
																	)
																) {
																	setRateInvalid((prev) => ({
																		...prev,
																		[creator.id]: false,
																	}));
																}
																debouncedSaveFinalRates(creator);
															}}
															onKeyDown={(e) => {
																if (e.key === "Enter") {
																	e.preventDefault();
																	void saveFinalRates(creator);
																}
															}}
															onBlur={() => {
																void saveFinalRates(creator);
															}}
														/>
													</RateInputShell>
												)}
											</TableCell>
										);
									}
									if (col.key === "_finalConvertedRate") {
										const assignmentId = creator.assignmentId || creator.assignment?.id;
										const readOnly = isClientOnly || !canManage || !assignmentId;

										return (
											<TableCell key={`${creator.id}-${col.key}`}>
												{readOnly ? (
													(() => {
														const value =
															creator.finalConvertedRate ??
															creator.assignment?.finalConvertedRate;
														return value === null || value === undefined
															? "—"
															: `${finalRateConvertedCurrency} ${formatRateMoneyDisplayValue(value, finalRateConvertedCurrency)}`;
													})()
												) : (
													<RateInputShell $invalid={convertedRateInvalid[creator.id]}>
														<RateSuffix>{finalRateConvertedCurrency}</RateSuffix>
														<RateInput
															placeholder="0.00"
															value={
																convertedRateDrafts[creator.id] ??
																formatRateDisplayValue(
																	creator.finalConvertedRate ??
																		creator.assignment?.finalConvertedRate,
																	finalRateConvertedCurrency,
																)
															}
															onChange={(e) => {
																const nextValue = sanitizeRateInputValue(
																	e.target.value,
																	finalRateConvertedCurrency,
																);
																setConvertedRateDrafts((prev) => ({
																	...prev,
																	[creator.id]: formatRateDisplayValue(
																		nextValue,
																		finalRateConvertedCurrency,
																	),
																}));
																if (
																	convertedRateInvalid[creator.id] &&
																	isRateInputValid(
																		nextValue.trim(),
																		finalRateConvertedCurrency,
																	)
																) {
																	setConvertedRateInvalid((prev) => ({
																		...prev,
																		[creator.id]: false,
																	}));
																}
																debouncedSaveFinalRates(creator);
															}}
															onKeyDown={(e) => {
																if (e.key === "Enter") {
																	e.preventDefault();
																	void saveFinalRates(creator);
																}
															}}
															onBlur={() => {
																void saveFinalRates(creator);
															}}
														/>
													</RateInputShell>
												)}
											</TableCell>
										);
									}
									if (col.key === "_creatorInterest") {
										const interested = normalizeCreatorInterestStatus(creator.creatorInterested);
										return (
											<TableCell key={`${creator.id}-${col.key}`}>
												{canManage ? (
													<CreatorInterestSelect
														creatorId={creator.id}
														assignmentId={
															creator.assignment?.id || (creator as any).assignmentId
														}
														campaignId={campaignId}
														blockId={blockId}
														interested={creator.creatorInterested}
														onInterestChange={onInterestChange}
														showLabel={false}
														yesNoLabels
														borderless
													/>
												) : interested ? (
													<StatusBadge
														$variant={
															interested === "outreached"
																? "info"
																: interested === "negotiating"
																	? "warning"
																	: interested === "interested"
																		? "success"
																		: "error"
														}
													>
														{getCreatorInterestStatusLabel(interested)}
													</StatusBadge>
												) : (
													"—"
												)}
											</TableCell>
										);
									}
									if (col.key === "_rationale") {
										const recs: string[] = creator.recommendation || [];
										const rationale =
											recs.length > 0 ? recs.join(", ") : creator.rationaleForProposing || "";
										const aId = creator.assignmentId || creator.assignment?.id;
										return (
											<TableCell
												key={`${creator.id}-${col.key}`}
												style={{ textOverflow: "ellipsis", lineClamp: 2, WebkitLineClamp: 2 }}
											>
												{!isClientOnly && canManage && aId && !isOutreach ? (
													<AppTooltip
														blurPx={0}
														title={rationale || "Click to write rationale"}
														arrow={false}
														maxWidth={500}
													>
														<RationaleCellContent
															onClick={() => {
																setRationale({
																	creatorId: creator.id,
																	assignmentId: String(aId),
																	text: rationale,
																});
															}}
														>
															{rationale || "Write rationale"}
														</RationaleCellContent>
													</AppTooltip>
												) : (
													(rationale && (
														<AppTooltip
															blurPx={0}
															title={rationale || "No rationale available"}
															arrow={false}
															maxWidth={500}
														>
															{rationale || "None"}
														</AppTooltip>
													)) ||
													"—"
												)}
											</TableCell>
										);
									}
									if (col.key === "_approval") {
										const aId = creator.assignmentId || creator.assignment?.id;
										const approval = aId ? approvals[aId] : null;
										if (!approval) return <TableCell key={`${creator.id}-${col.key}`}>—</TableCell>;
										const rawStatus = approval.status;
										const status: "approved" | "rejected" | "pending" =
											rawStatus === "approved"
												? "approved"
												: rawStatus === "rejected"
													? "rejected"
													: "pending";
										const label =
											status === "approved"
												? "Approved"
												: status === "rejected"
													? "Declined"
													: "Pending";
										const icon =
											status === "approved" ? (
												<CheckOutlined />
											) : status === "rejected" ? (
												<CloseOutlined />
											) : (
												<ClockCircleOutlined />
											);
										return (
											<TableCell key={`${creator.id}-${col.key}`}>
												<ApprovalCell $status={status}>
													{icon}
													{label}
												</ApprovalCell>
											</TableCell>
										);
									}
									if (col.key === "_comment") {
										const aId = creator.assignmentId || creator.assignment?.id;
										const approval = aId ? approvals[aId] : null;
										return (
											<TableCell
												key={`${creator.id}-${col.key}`}
												style={{ textOverflow: "ellipsis", lineClamp: 2, WebkitLineClamp: 2 }}
											>
												<AppTooltip
													blurPx={0}
													title={approval?.comment || "No comment yet"}
													arrow={false}
													maxWidth={500}
												>
													{approval?.comment || "—"}
												</AppTooltip>
											</TableCell>
										);
									}
									return <TableCell key={`${creator.id}-${col.key}`}>—</TableCell>;
								})}
								<TableCell>
									<SmallToggle
										$active={creatorVisibility[creator.id] || false}
										$disabled={false}
										onClick={() => onCreatorVisibilityToggle(creator.id)}
									/>
								</TableCell>
								{!isOutreach && (
									<TableCell style={{ minWidth: 120 }}>
										<ActionIcons>
											<SettingsIcon
												name="setting"
												alt="Settings"
												onClick={() => onSettingsClick(creator.id)}
											/>
											{!isClientOnly && (
												<Dropdown
													trigger={["click"]}
													placement={"bottom"}
													getPopupContainer={() => document.body}
													dropdownRender={() => (
														<StyledDropdownMenu>
															{canManage && !isOutreach && (
																<StyledDropdownItem
																	onClick={() => {
																		const aId =
																			creator.assignmentId ||
																			creator.assignment?.id;
																		const recs: string[] =
																			creator.recommendation || [];
																		const text =
																			recs.length > 0
																				? recs.join(", ")
																				: creator.rationaleForProposing || "";
																		setRationale({
																			creatorId: creator.id,
																			assignmentId: String(aId),
																			text,
																		});
																	}}
																>
																	<EditOutlined
																		style={{ color: "#35373d", fontSize: 16 }}
																	/>
																	<span>Write Rationale</span>
																</StyledDropdownItem>
															)}
															{onRemoveCreator && creator.assignmentId && (
																<StyledDropdownItem
																	onClick={() => {
																		onRemoveCreator(
																			String(creator.assignmentId),
																			creator.name,
																		);
																	}}
																>
																	<DeleteOutlined
																		style={{ color: "#D30024", fontSize: 16 }}
																	/>
																	<span style={{ color: "#D30024" }}>Remove</span>
																</StyledDropdownItem>
															)}
														</StyledDropdownMenu>
													)}
												>
													<MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
												</Dropdown>
											)}
										</ActionIcons>
									</TableCell>
								)}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			{/* Rationale modal */}
			{rationale && (
				<RationaleModal
					open={!!rationale}
					initialValue={rationale.text}
					onCancel={() => setRationale(null)}
					loading={submittingRationale}
					onSave={async (text: string) => {
						if (!campaignId || !blockId || !rationale) {
							message.error("Missing campaign or block");
							return;
						}
						setSubmittingRationale(true);
						try {
							await creatorListAPI.updateRecommendation(campaignId, blockId, rationale.assignmentId, [
								text,
							]);
							message.success("Rationale saved");
							if (onUpdateCreator) {
								onUpdateCreator({ id: String(rationale.creatorId), recommendation: [text] });
							}
							setRationale(null);
						} catch (err) {
							console.error(err);
						} finally {
							setSubmittingRationale(false);
						}
					}}
				/>
			)}
		</TableContainer>
	);
};
